'use strict';

import VZAddBase from './VZAddBase';
import { showErrorModal } from 'Root/modal';
import { tryParseJSON } from 'app_vodafone_ziggo/util/util';

export default class VZAddTeamsCalling extends VZAddBase {
    constructor() {
        super();

        this.configuratorType = this.dataset.configuratorType;
        this.addToCartUrl = this.dataset.addToCartUrl;
        this.removeFromCartUrl = this.dataset.removeFromCartUrl;
    }

    connectedCallback() {
        this.addEventListener('click', this.onClick);
    }

    disconnectedCallback() {
        this.removeEventListener('click', this.onClick);
    }

    /**
     * @function submitForm
     * @description Submits the product form when the add to basket modal submit button is clicked.
     * @param {Event} event - A click event on the add to basket modal submit button.
     */
    async submitForm(event) {
        event.preventDefault();

        this.spinner.start();

        const selectedOptionalBusinessRulesAddons = this.constructor.getAllProducts();
        const optionalBusinessRulesAddonsInCart = JSON.parse(this.dataset.nonCsItemsInCart);

        const toBeAdded = selectedOptionalBusinessRulesAddons.filter((addon) => !optionalBusinessRulesAddonsInCart.some(({ id }) => id === addon.pid));

        if (toBeAdded.length > 0) {
            await this.submitLinkedProducts.bind(this)(toBeAdded, false, this.addToCartUrl, 'POST');
        }

        const formData = new FormData();
        const addToCartValues = this.constructor.getAddToCartValues();
        formData.set('pid', addToCartValues.pid);
        formData.set('quantity', addToCartValues.linesQty);
        formData.set('numberPorting', addToCartValues.porting);
        formData.set('linesQty', addToCartValues.linesQty);
        formData.set('usersQty', addToCartValues.usersQty);
        formData.set('addons', addToCartValues.addons);
        formData.set('addonsQty', addToCartValues.addonsQty);
        formData.set('optionalBusinessRulesAddons', JSON.stringify(toBeAdded));
        formData.set('configuratorType', 'teams-calling');
        formData.set('activeConfiguration', addToCartValues.activeConfiguration);

        fetch(this.form.action, {
            method: 'POST',
            body: formData
        })
            .then((response) => response.json())
            .then((response) => {
                if (!response.success) throw new Error('Error');

                // After having added the product to the cart, show the "added to basket" modal or journey modal
                const modal = document.getElementById('add-to-basket-modal');

                if (response.hideAddToBasketModal) {
                    window.location.href = response.basketUrl;
                } else {
                    modal.render({});
                }

                /* eslint-disable no-underscore-dangle */
                if (response.ecommerceObject && window._ddm) {
                    window._ddm.trigger('ecommerce.add_to_cart', {
                        data: response.ecommerceObject.ecommerce.addToCart
                    });
                }
                /* eslint-enable no-underscore-dangle */
            })
            .catch(() => {
                this.constructor.handleNetworkError();
            })
            .finally(() => {
                this.spinner.stop();
            });
    }

    /**
     * @function onClick
     * @description Handles the onClick
     * @param {Event} event - The click event fired from the add to basket button.
     */
    async onClick(event) {
        event.preventDefault();
        this.classList.add('pe-none');

        // If we are in editing mode, remove the old location first
        if (this.editMode) {
            await fetch(document.querySelector('#remove-group').action, {
                method: 'POST'
            });
        }

        const submitBtn = this.querySelector('.js-configurator-submit-btn');
        if (submitBtn && !submitBtn.hasAttribute('disabled')) {
            await this.submitForm.call(this, event);
        }
    }

    /**
     * @function handleNetworkError
     * @description This function is called when adding to the basket for business internet products returns a 500.
     */
    static handleNetworkError() {
        const { title, body, cta } = JSON.parse(document.querySelector('vz-tc-product-card').dataset.generalError);

        showErrorModal(title, body, cta);
    }

    /**
     * @function submitLinkedProducts
     * @return {Promise} - A resolved promise
     * @param {Array} products - Array of linked products
     * @param {boolean} inJourney - Is in journey
     * @param {string} url - Request URL
     * @param {string} type - Type of Request
     * @description Submits all the products which are linked to the configured one.
     */
    submitLinkedProducts(products, inJourney, url, type) {
        const formData = new FormData();
        formData.set('productData', JSON.stringify(products));
        formData.set('belongsToGuidedJourney', inJourney);

        // If the previous call returned csSolutionId, this call has to use the same solution
        if (this.csSolutionId && this.csSolutionId !== null) {
            formData.set('csSolutionId', this.csSolutionId);
        }

        return fetch(url, {
            method: type,
            ...(type === 'POST' && { body: formData })
        })
            .then((response) => response.json())
            .then((response) => {
                if (response && response.csSolutionId) this.csSolutionId = response.csSolutionId;
            })
            .catch(() => {
                this.spinner.stop();
                this.constructor.handleNetworkError();
            });
    }

    static getAddToCartValues() {
        const addToCartValues = document.querySelector('.js-add-to-cart-values');
        if (!addToCartValues) return {};
        const addons = tryParseJSON(addToCartValues.dataset.addons, []);
        const fullPidAddons = addons.map((addonId) => {
            if (!addonId.includes(addToCartValues.dataset.pid)) {
                return `${addonId}_${addToCartValues.dataset.pid}`;
            }
            return addonId;
        });
        return {
            pid: addToCartValues.dataset.pid,
            linesQty: addToCartValues.dataset.linesqty,
            usersQty: addToCartValues.dataset.usersqty,
            addons: JSON.stringify(fullPidAddons),
            addonsQty: addToCartValues.dataset.addonsQty,
            porting: addToCartValues.dataset.porting,
            activeConfiguration: addToCartValues.dataset.activeConfiguration
        };
    }
}

if (!window.customElements.get('vz-add-teams-calling')) {
    window.customElements.define('vz-add-teams-calling', VZAddTeamsCalling);
}
